import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  debounce,
  isInScrollerX,
  onAnimationEnd,
  isClickedOutside,
  isPointWithinElement,
} from '@theme/utilities';
import { lock, unlock } from 'tua-body-scroll-lock';

/**
 * A custom element that manages a dialog.
 *s
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = (this.#controller = new AbortController());

    this.addEventListener('touchmove', this.#handleTouchMove, {
      capture: true,
      passive: false,
      signal,
    });

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    // A pending debounce keeps this component alive and would close a dialog already gone.
    this.#handleResize.cancel();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /**
   * Corrects two ways tua-body-scroll-lock misreads touches on a modal dialog. Runs in the
   * capture phase on the host so it is guaranteed to fire before tua's handler on the dialog.
   *
   * Backdrop: `::backdrop` is a pseudo-element, so touches on the overlay report the dialog
   * itself as their target. tua then measures the dialog's own scroll boundary and, whenever
   * the dialog is scrollable and mid-scroll, stops propagation to let the gesture through —
   * scrolling the page behind instead. Cancel those outright.
   *
   * Horizontal scrollers: tua only checks the lock target's boundary, so a horizontal swipe
   * inside an `overflow-x` descendant reads as an edge scroll and gets killed by tua's
   * document-level preventDefault. Stop propagation to let it reach native scrolling.
   */
  #handleTouchMove = (/** @type {Event} */ event) => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    const touch = /** @type {TouchEvent} */ (event).targetTouches[0];

    if (touch && !isPointWithinElement(touch.clientX, touch.clientY, dialog)) {
      event.preventDefault();
      return;
    }

    if (isInScrollerX(/** @type {Element | null} */ (event.target), dialog)) {
      event.stopPropagation();
    }
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** @type {HTMLElement | null} */
  #previousActiveElement = null;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lock(dialog);

      dialog.showModal();
      trapFocus(dialog);
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlock(dialog);

    const previousActive =
      this.#previousActiveElement && document.body.contains(this.#previousActiveElement)
        ? this.#previousActiveElement
        : null;

    dialog.close();
    removeTrapFocus(previousActive);
    this.#previousActiveElement = null;
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
